import { PaymentPage } from "./place-order-api";

import { ErrorCode } from './errorUtils';
import Request from "../../utils/request";

const getClubsparkApiDomain = (context = window) => context.lta.clubspark.checkout.clubsparkApiDomain;
const getClubsparkRequestHeaders = (context = window) => ({
	'Authorization': `ClubSpark-Auth ${context.lta.clubspark.user.token}`,
	'Content-Type': 'application/json'
});

const captureCardPayment = async (paymentIntentId) => {
	return await Request.post('/api/payments/capture', {
		PaymentIntentId: paymentIntentId
	});
}

// Ideally this should sit in a UI-related area of the codebase.
const setProcessingState = (isProcessing) => {
	const loadingClasses = ['lta-loader', 'in-out-animation'];
	const { loadingTimeoutId } = window.lta.clubspark.checkout;
	const container = document.body;

	if (loadingTimeoutId) {
		clearTimeout(loadingTimeoutId);
		window.lta.clubspark.checkout.loadingTimeoutId = undefined;
	}

	if (isProcessing) {
		// Delay the showing of the loader briefly to improve UX for validation & fast responses
		window.lta.clubspark.checkout.loadingTimeoutId = setTimeout(() => {
			container.classList.add(...loadingClasses);
		}, 200);
	} else {
		container.classList.remove(...loadingClasses);
	}
};

// prettier-ignore
const handleTransactionError = (errorCallback) =>
	// eslint-disable-next-line no-use-before-define
	(error, errorCode = errorCode.UnknownError) => {
		setProcessingState(false);

		const callbackError = new Error(error.message || 'Transaction was unsuccessful');
		callbackError.errorCode = errorCode;
		callbackError.isSubmissionError = errorCode !== errorCode.FieldValidationError;

		// eslint-disable-next-line no-console
		console.error(`Error: ${callbackError.message}`, callbackError);
		errorCallback(callbackError);
	};

/*
 * @param {object} clubSparkResponse Object returned from ClubSpark's API
 * @param {object} [clubSparkResponse.Error] Object containing the error if one occured
 * @param {string} [clubSparkResponse.ID] a Stripe paymentIntentId
 * @param {string} [clubSparkResponse.ExternalID] a Stripe clientSecret
 * @param {string} [clubSparkResponse.Status] One of: requires_payment_method, requires_confirmation, requires_action, processing, canceled, or succeeded
 * @param {boolean} [clubSparkResponse.RequiresAction] True if a two factor authentication challenge is required
 */
// prettier-ignore
const handleTransactionSuccess = (successCallback) =>
	async ({ Error, ExternalID, ID, RequiresAction, Status }, extraParameter) => {
		await successCallback({
			error: Error,
			externalId: ExternalID,
			id: ID,
			requiresAction: RequiresAction,
			status: Status,
			extraParam: extraParameter,
			provider:'clubspark'
		});
		setProcessingState(false);
	};

const confirmPayment = async (stripe, paymentIntentId, successCallback, errorCallback, extraParameter) => {
	const paymentConfirmationEndpoint = `${getClubsparkApiDomain()}/payment/confirmpayment`;
	const paymentConfirmationResponse = await fetch(paymentConfirmationEndpoint, {
		method: 'POST',
		headers: getClubsparkRequestHeaders(),
		body: JSON.stringify({ PaymentIntentId: paymentIntentId })
	});
	handleServerResponse(
		stripe,
		await paymentConfirmationResponse.json(),
		successCallback,
		errorCallback,
		extraParameter
	);
};

const createPaymentMethod = (paymentGateway, type, user, paymentMethodArguments) => {
	const methodParameters = {
		type,
		billing_details: {
			name: user.fullName,
			email: user.email
		}
	};

	if (type === 'card') {
		methodParameters.card = paymentMethodArguments.card;

		if (paymentMethodArguments.postCode) {
			methodParameters.billing_details.address = {
				postal_code: paymentMethodArguments.postCode
			};
		}
	}

	return paymentGateway.createPaymentMethod(methodParameters);
};

const createCardPaymentMethod = (paymentGateway, user, cardNumberElement, postCodeValue) => {
	const paymentMethodArguments = {
		card: cardNumberElement,
		postCode: postCodeValue
	};

	return createPaymentMethod(paymentGateway, 'card', user, paymentMethodArguments);
};

const createPaymentRecord = ({
	description,
	isCourse,
	paymentMethodId,
	scopeId,
	totalCost,
	userId,
	venueId,
	checkoutType
}) => {
	const paymentCreationEndpoint = `${getClubsparkApiDomain()}/payment/createpayment`;
	let paymentParameters = isCourse ? 'CoachingModule' : 'BookingModule';
	if (checkoutType === 'youth start') {
		paymentParameters = 'platform';
	}
	return fetch(paymentCreationEndpoint, {
		method: 'POST',
		headers: getClubsparkRequestHeaders(),
		body: JSON.stringify({
			Cost: totalCost,
			Description: description,
			Metadata: null, // eslint-disable-line unicorn/no-null
			PaymentMethodId: paymentMethodId,
			PaymentParams: JSON.stringify([paymentParameters]),
			ScopeID: scopeId,
			UserID: userId,
			VenueID: venueId
		})
	});
};

/**
 * @param {object} paymentGateway An instance of ClubSparkPaymentGateway to interact with for payment
 * @param {object} clubSparkResponse Object returned from ClubSpark's API
 * @param {object} [clubSparkResponse.Error] Object containing the error if one occured
 * @param {string} [clubSparkResponse.ID] a Stripe paymentIntentId
 * @param {string} [clubSparkResponse.ExternalID] a Stripe clientSecret
 * @param {boolean} [clubSparkResponse.RequiresAction] True if a two factor authentication challenge is required
 * @param {string} [clubSparkResponse.Status] One of: requires_payment_method, requires_confirmation, requires_action, processing, canceled, or succeeded
 * @param {Function} successCallback Function to be called when the process has been successful
 * @param {Function} errorCallback Function to be called in the event of an error
 * @param extraParam
 * @param extraParameter
 */
async function handleServerResponse(paymentGateway, clubSparkResponse, successCallback, errorCallback, extraParameter) {
	const handleError = handleTransactionError(errorCallback);
	if (clubSparkResponse.Error) {
		// eslint-disable-next-line no-undef
		handleError(clubSparkResponse.Error, ErrorCode.ClubSparkApiError);
	} else if (clubSparkResponse.RequiresAction) {
		const stripeActionResult = await paymentGateway.handleCardAction(clubSparkResponse.ExternalID);
		if (stripeActionResult.error) {
			// eslint-disable-next-line no-undef
			handleError(stripeActionResult.error, ErrorCode.StripeActionError);
		} else {
			// The card action has been handled, the PaymentIntent can be confirmed again on the server
			const confirmationResult = await confirmPayment(
				paymentGateway,
				clubSparkResponse.ID,
				successCallback,
				errorCallback,
				extraParameter
			);
			if (!confirmationResult.ok) {
				// eslint-disable-next-line no-undef
				handleError({ message: 'Could not confirm result' }, ErrorCode.NetworkError);
			}
		}
	} else {
		handleTransactionSuccess(successCallback)(clubSparkResponse, extraParameter);
	}
}

export const createPayment = async ({ paymentMethodId }) => {
	const { description, scopeId, totalCost, venueId, isCourseBooking, checkoutType } = window.lta.clubspark.basket;
	const { id: userId } = window.lta.clubspark.user;

	try {
		const paymentCreationResponse = await createPaymentRecord({
			description,
			isCourse: isCourseBooking,
			paymentMethodId,
			scopeId,
			totalCost,
			userId,
			venueId,
			checkoutType
		});
		return {
			success: paymentCreationResponse.ok,
			response: await paymentCreationResponse.json()
		};
	} catch {
		return {};
	}
};

export const takeCardPayment = async (
	paymentGateway,
	cardNumberElement,
	postCodeState,
	paymentGateway3DSecureInstance,
	successCallback,
	errorCallback,
	extraParameter,
	paymentStartCallback,
	paymentEndCallback,
	securePaymentStartCallback,
	securePaymentEndCallback
) => {
	setProcessingState(true);
	paymentStartCallback?.();
	const handleError = handleTransactionError(errorCallback);
	const paymentMethodResult = await createCardPaymentMethod(
		paymentGateway,
		window.lta.clubspark.user,
		cardNumberElement,
		postCodeState.value
	);

	if (paymentMethodResult.error || postCodeState.isInvalid) {
		const code = paymentMethodResult?.error?.decline_code
			? ErrorCode.PaymentMethodCreationError
			: ErrorCode.FieldValidationError;
		handleError({ message: paymentMethodResult?.error?.message || 'Error with the payment method' }, code);
	} else {
		const handleCreateRecordError = () =>
			// eslint-disable-next-line no-undef
			handleError({ message: 'Could not create payment record' }, ErrorCode.NetworkError);

		// TODO: Based on Clubspark flag
		const shouldInformClubspark = ![PaymentPage.LICENCE_RENEWAL, PaymentPage.ACCREDITATION_RENEWAL].includes(window?.lta?.checkout?.screen);
		if (shouldInformClubspark) {
			const { success, response } = await createPayment({
				paymentMethodId: paymentMethodResult.paymentMethod.id,
				errorCallback: handleCreateRecordError
			});
			if (success) {
				handleServerResponse(paymentGateway, response, successCallback, errorCallback, extraParameter);
			} else {
				handleCreateRecordError();
			}
		} else {
			const order = await successCallback({
				id: paymentMethodResult?.paymentMethod?.id,
				extraParam: { ...window.lta?.checkout?.params, ...extraParameter },
				screen: window.lta?.checkout?.screen
			});
			if(order?.Status === "REQ_AUTHORIZATION") {
				try {
					securePaymentStartCallback?.();
					const { paymentIntent, error } = await threeDSecureHandler(order, paymentGateway3DSecureInstance);
					securePaymentEndCallback?.();
					if(error) {
						const cancelPayment = await captureCardPayment(error.payment_intent.id);
						console.error("cancelPayment", cancelPayment);
						handleError({ message: 'We were unable to confirm your card payment.', ...error }, ErrorCode.StripeActionError);
					} else {
						const capturePayment = await captureCardPayment(paymentIntent.id);
						successCallback({
							id: paymentMethodResult?.paymentMethod?.id,
							extraParam: { ...window.lta?.checkout?.params, ...extraParameter, IS_REQ_AUTHORIZATION_SUCCESS: true },
							screen: window.lta?.checkout?.screen,
						});
					}
				} catch(error) {
					handleError({ message: 'We were unable to confirm your card payment.', ...error }, ErrorCode.StripeActionError);
					securePaymentEndCallback?.();
				}
			}
		}
		paymentEndCallback?.();
	}
};

export const threeDSecureHandler = async (paymentRequest, paymentGateway3DSecureInstance) => {
	return await paymentGateway3DSecureInstance
			.confirmCardPayment(paymentRequest.ClientSecret)
			.catch(() => {
				
			});
}

export const takeWalletPayment = async (
	paymentGateway,
	paymentMethodId,
	successCallback,
	errorCallback,
	extraParameter
) => {
	const handleError = handleTransactionError(errorCallback);

	// eslint-disable-next-line no-undef
	const handlePaymentError = () => handleError({ message: 'Could not create payment record' }, ErrorCode.NetworkError);

	// TODO: Based on Clubspark flag
	let shouldInformClubspark = ![PaymentPage.LICENCE_RENEWAL, PaymentPage.ACCREDITATION_RENEWAL, PaymentPage.YOUTH_STARTER_OFFER].includes(window?.lta?.checkout?.screen);
	if(shouldInformClubspark) {

		const { success, response } = await createPayment({
			paymentMethodId,
			errorCallback: handlePaymentError
		});

		if (success) {
			handleServerResponse(paymentGateway, response, successCallback, errorCallback, extraParameter);
		} else {
			handlePaymentError();
		}
	} else {
		successCallback({paymentMethodId, extraParam: extraParameter, screen: window.lta?.checkout?.screen})
	}
};
